/**
 * Choices and the action box (the live turn's last checkbox) — the one part of this screen a
 * running turn is allowed to take away.
 *
 * A single global `isLoading` used to take everything with it: you could not reread chapter
 * 3, open the world panel or change a setting for ~50 seconds. The live turn replaces that with a rule
 * narrow enough to state in one line, and this component is where the whole of it lives:
 * **acting is what a turn takes away; nothing else is.**
 *
 * **And it takes it away by unmounting this, not by disabling it** — `PlayRoute` renders no
 * action bar at all while a turn of this story's is live, and its header argues why. Two
 * consequences here. The text box no longer stays live through the wait: composing the next
 * action mid-turn was a real use of those fifty seconds, and it lost to the simpler fact that
 * the reader is reading and a shelf of dead controls under the paragraph is in the way. And
 * the `disabled` state that remains has exactly one cause left — this client's one turn slot
 * being held by a turn on *another* story — which is a sentence this bar has to say itself,
 * because nothing about that turn is on this screen.
 *
 * The reason is wired through `aria-describedby` so it is announced with the control rather
 * than floating somewhere near it.
 *
 * **The box's value is not local state.** It lives in `turn/draft.ts`, so the
 * character panel can write *"drink from the water bottle"* into it from the other side of the
 * screen. Slice 2 left this deliberately unplumbed — the note here said inventing the store
 * before the panel that writes to it existed was how it would end up the wrong shape — and
 * what the panel turned out to need was not a setter but a *focus* signal, which is why the
 * store counts writes rather than just holding a string.
 *
 * **The suggested actions are `Choices.tsx`, not this file.** They stopped being a row of
 * chips that submit when they became cards that can also be *amended* into the box, and the
 * two halves of that — the number-key shortcut and the pencil that writes through
 * `draft.ts` — are a component's worth of behaviour rather than a `map` in the middle of a
 * form. What stays here is the seam: `submit` is handed down, so a choice and a typed
 * sentence still take exactly the same path out.
 *
 * **A dead story renders no action bar at all.** The epitaph used to be printed here, as the
 * half of the death beat that could not wait; the death beat landed and `DeathBeat` owns the ending now. Read-only
 * is expressed by the controls not existing rather than by them being disabled — there is
 * nothing a dead character is *about* to be able to do.
 */
import { useEffect, useRef } from 'react';
import { Choices } from '@/features/turn/Choices';
import { useDraftStore } from '@/features/turn/draft';
import { useT } from '@/i18n/context';

interface ActionBarProps {
  /** `MIN_CHOICES`/`MAX_CHOICES` in `quest/llm/tools.py` — 3 to 6. Empty before the opening
   * turn and after death. */
  choices: readonly string[];
  /**
   * False while any turn resolves. The only input to the disabled state — and by the time this
   * component exists at all, the only turn that can make it false belongs to another story.
   */
  canSubmit: boolean;
  /** Why, in words. Rendered under the controls it describes. */
  disabledReason: string | null;
  /** `chapter_count === 0`: the one state a null action is legal in (P4). */
  opening: boolean;
  onSubmit: (action: string | null) => void;
}

export function ActionBar({
  choices,
  canSubmit,
  disabledReason,
  opening,
  onSubmit,
}: ActionBarProps) {
  const t = useT();
  const draft = useDraftStore((state) => state.text);
  const writes = useDraftStore((state) => state.writes);
  const box = useRef<HTMLInputElement | null>(null);

  /**
   * Take the caret to the end of a suggestion, so the player's next keystroke continues the
   * sentence instead of overwriting it.
   *
   * Keyed on `writes` rather than on the text: a second click on the same item changes no
   * string, and it must still bring the box back.
   *
   * **The ref is initialised to the current count, so a *mount* never focuses**, only a move.
   * That used to be `writes === 0`, which was the same thing back when this component was
   * mounted for the whole story. It is not any more — the bar is unmounted for the length of
   * every turn — so a count left over from a suggestion three chapters ago would grab focus
   * from wherever the reader had put it, once per turn, forever.
   */
  const focused = useRef(writes);

  useEffect(() => {
    if (writes === focused.current) return;
    focused.current = writes;
    const input = box.current;
    if (!input) return;
    input.focus();
    input.setSelectionRange(input.value.length, input.value.length);
  }, [writes]);

  const submit = (action: string | null): void => {
    if (!canSubmit) return;
    onSubmit(action);
    useDraftStore.getState().clear();
  };

  const ownReason = disabledReason !== null;
  const reasonId = !canSubmit && ownReason ? REASON_ID : undefined;

  return (
    // Not sticky itself: the story screen puts it inside the story screen's sticky foot, under the character
    // peek row, so that the two are one block a phone cannot overlap with itself.
    <div className="bg-bg/95 border-rule border-t backdrop-blur">
      <div className="mx-auto w-full max-w-[var(--measure)] px-5 py-4">
        {opening ? (
          <button
            type="button"
            disabled={!canSubmit}
            onClick={() => {
              submit(null);
            }}
            aria-describedby={reasonId}
            className="border-accent text-accent hover:bg-accent enabled:hover:text-bg w-full border px-4 py-2.5 font-mono transition-colors disabled:opacity-40"
            style={{ fontSize: 'var(--text-action)' }}
          >
            {t('ui.turn.wake_up')}
          </button>
        ) : (
          <>
            {choices.length > 0 ? (
              <Choices
                choices={choices}
                canSubmit={canSubmit}
                describedBy={reasonId}
                onSubmit={submit}
              />
            ) : null}

            <form
              className="flex gap-2"
              onSubmit={(event) => {
                event.preventDefault();
                const action = draft.trim();
                if (action.length > 0) submit(action);
              }}
            >
              <input
                ref={box}
                type="text"
                value={draft}
                onChange={(event) => {
                  useDraftStore.getState().type(event.target.value);
                }}
                // Never disabled. Where this bar exists at all the player may act, and the
                // one exception (another story's turn) is a wait worth composing through —
                // only the submit path can spend money.
                placeholder={t('ui.turn.action_placeholder')}
                aria-label={t('ui.turn.action_label')}
                className="border-border focus:border-accent text-fg placeholder:text-fg-faint min-w-0 grow border bg-transparent px-3 py-2 font-mono transition-colors outline-none"
                style={{ fontSize: 'var(--text-action)' }}
              />
              <button
                type="submit"
                disabled={!canSubmit || draft.trim().length === 0}
                aria-describedby={reasonId}
                className="border-accent text-accent hover:bg-accent enabled:hover:text-bg shrink-0 border px-4 py-2 font-mono transition-colors disabled:opacity-40"
                style={{ fontSize: 'var(--text-action)' }}
              >
                {t('ui.turn.submit')}
              </button>
            </form>
          </>
        )}

        {!canSubmit && ownReason ? (
          <p
            id={REASON_ID}
            className="text-fg-faint mt-2 font-mono"
            style={{ fontSize: 'var(--text-meta)' }}
          >
            {disabledReason}
          </p>
        ) : null}
      </div>
    </div>
  );
}

const REASON_ID = 'action-disabled-reason';
